/**
 * tool-file-info.js — runtime behaviour for the <x-file-info> blade component.
 *
 * Public API (window.ToolFileInfo):
 *   show(idOrEl, file, { onRemove, onReplace, fallbackIconType }) — render the
 *     card, kick off progressive probing, wire the × button + replace-on-drop.
 *   hide(idOrEl) — hide the card.
 *   probe(file)  — run the full probe pipeline and resolve to a meta object.
 *                  Useful standalone (e.g. tool wants to show codec details in
 *                  its own UI without using the card).
 *
 * Probe stages — each stage fills what it can, card updates as results arrive:
 *   1. Instant — File: name, extension, size, detected type.
 *   2. ~50-200 ms — native <audio>/<video> loadedmetadata: duration, resolution.
 *   3. ~200-800 ms — Web Audio decodeAudioData on the first MB of an audio file:
 *                    sample rate, channels. Skipped for videos (the video's
 *                    audio track needs ffprobe, not Web Audio).
 *   4. ~1-2 s — FFmpegProcessor.probeStreaming (WORKERFS, no heap copy): codec
 *               names, precise bitrate, fps, channel layout. Only fires if the
 *               processor is already loaded OR the tool already needs it —
 *               otherwise we don't spin up 30 MB of WASM just to show a bitrate.
 *
 * Graceful degradation: every phase is wrapped; if one fails, the chips it
 * would have filled stay hidden and the rest keep filling in.
 */
(function(){
'use strict';

function $(idOrEl){ return typeof idOrEl === 'string' ? document.getElementById(idOrEl) : idOrEl; }

// i18n strings. Blade layout puts them on window.__TFI_I18N before this module runs.
function T(key, fallback){
    var dict = window.__TFI_I18N || {};
    return dict[key] || fallback || key;
}

// ───── Formatters ─────

function fmtSize(bytes){
    if (!bytes) return '';
    if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(bytes >= 10737418240 ? 0 : 1) + ' GB';
    if (bytes >= 1048576)    return (bytes / 1048576).toFixed(bytes >= 104857600 ? 0 : 1) + ' MB';
    if (bytes >= 1024)       return Math.round(bytes / 1024) + ' KB';
    return bytes + ' B';
}

function fmtDuration(seconds){
    if (!isFinite(seconds) || seconds < 0) return '';
    var h = Math.floor(seconds / 3600);
    var m = Math.floor((seconds % 3600) / 60);
    var s = Math.floor(seconds % 60);
    if (h > 0) return h + ':' + String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
    return m + ':' + String(s).padStart(2, '0');
}

function fmtChannels(n){
    if (typeof n === 'string') return n;
    if (n === 1) return T('mono', 'Mono');
    if (n === 2) return T('stereo', 'Stereo');
    if (n === 6) return '5.1';
    if (n === 8) return '7.1';
    if (n) return n + ' ch';
    return '';
}

function fmtBitrate(kbps){ return kbps > 0 ? kbps + ' kbps' : ''; }
function fmtFps(fps){ return fps > 0 ? Math.round(fps * 100) / 100 + ' fps' : ''; }
function fmtSampleRate(hz){ return hz > 0 ? (hz / 1000).toFixed(hz % 1000 === 0 ? 0 : 1) + ' kHz' : ''; }

// ───── Type detection ─────

var AUDIO_EXT = ['mp3','wav','ogg','flac','aac','m4a','wma','aiff','aif','opus','oga','amr','caf'];
var VIDEO_EXT = ['mp4','mov','mkv','avi','webm','flv','wmv','m4v','3gp','ts','mts','m2ts'];
var IMAGE_EXT = ['jpg','jpeg','png','gif','webp','bmp','heic','heif','tiff','tif','avif'];

function fileType(file){
    var t = (file && file.type || '').toLowerCase();
    if (t.indexOf('audio/') === 0) return 'audio';
    if (t.indexOf('video/') === 0) return 'video';
    if (t.indexOf('image/') === 0) return 'image';
    var ext = (file && file.name || '').split('.').pop().toLowerCase();
    if (AUDIO_EXT.indexOf(ext) !== -1) return 'audio';
    if (VIDEO_EXT.indexOf(ext) !== -1) return 'video';
    if (IMAGE_EXT.indexOf(ext) !== -1) return 'image';
    return 'file';
}

// ───── Icons ─────
//
// Each SVG carries INTRINSIC width/height="28" attributes — NOT only the
// `.tfi-icons svg { width:28px }` CSS rule. Reason (req 2026-06-14, YaBrowser
// RU, screenshot): `css/tool-file-info.css` is loaded async (media="print" →
// swap to all). For a steady ~8–18 users/hour that async swap never completes
// (network blip, the print→all onload not firing, an extension stripping the
// link) and the stylesheet is absent. A viewBox-only SVG with no width/height
// then has NO intrinsic size — the browser falls back to the replaced-element
// default and it expands to fill its flex parent, rendering the audio note at
// full page width ("слишком большой значок на всю страницу"). The attribute is
// the floor; the CSS rule still wins when present (and can resize responsively).
// Defense in depth: an icon must never depend SOLELY on an async CSS file for
// a non-catastrophic size.

var ICONS = {
    audio: '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>',
    video: '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>',
    image: '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>',
    file:  '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>',
};

function renderIcons(container, info){
    container.innerHTML = '';
    var mainIcon = document.createElement('span');
    mainIcon.className = 'tfi-icon';
    mainIcon.innerHTML = ICONS[info.type] || ICONS.file;
    container.appendChild(mainIcon);
    // Dual-icon for videos that also carry audio — hint at both streams
    if (info.type === 'video' && info.acodec) {
        var aIcon = document.createElement('span');
        aIcon.className = 'tfi-icon tfi-icon-secondary';
        aIcon.innerHTML = ICONS.audio;
        aIcon.style.opacity = '.55';
        aIcon.style.marginInlineStart = '-6px';
        container.appendChild(aIcon);
    }
}

// ───── Probing ─────

function probeInstant(file){
    return {
        name: file.name || 'file',
        size: file.size || 0,
        type: fileType(file),
        ext: (file.name || '').split('.').pop().toUpperCase(),
    };
}

function probeNativeMedia(file, info){
    return new Promise(function(resolve){
        if (info.type !== 'audio' && info.type !== 'video') { resolve(info); return; }
        var media = document.createElement(info.type === 'video' ? 'video' : 'audio');
        var url = URL.createObjectURL(file);
        var done = false;
        var timer = setTimeout(function(){ if(!done){ done=true; cleanup(); resolve(info); } }, 4000);
        function cleanup(){ try { URL.revokeObjectURL(url); } catch(_){} media.removeAttribute('src'); }
        media.preload = 'metadata';
        media.muted = true;
        media.onloadedmetadata = function(){
            if (done) return;
            done = true;
            clearTimeout(timer);
            if (isFinite(media.duration) && media.duration > 0) info.duration = media.duration;
            if (info.type === 'video') {
                if (media.videoWidth)  info.width  = media.videoWidth;
                if (media.videoHeight) info.height = media.videoHeight;
            }
            cleanup();
            resolve(info);
        };
        media.onerror = function(){
            if (done) return;
            done = true;
            clearTimeout(timer);
            cleanup();
            resolve(info);
        };
        media.src = url;
    });
}

async function probeWebAudio(file, info){
    if (info.type !== 'audio') return info;
    if (!(window.AudioContext || window.webkitAudioContext)) return info;
    try {
        // First 2 MB is enough for header + a few frames — avoids loading a 200 MB file
        // into a float32 PCM buffer just to read sample rate and channel count.
        var head = file.slice(0, Math.min(file.size, 2 * 1024 * 1024));
        var ab = await head.arrayBuffer();
        var Ctor = window.AudioContext || window.webkitAudioContext;
        var ctx = new Ctor();
        window.__suppressDecodeFallback = true;    // we only want native codec info
        try {
            var buf = await ctx.decodeAudioData(ab);
            if (buf.sampleRate) info.sampleRate = buf.sampleRate;
            if (buf.numberOfChannels) info.channels = buf.numberOfChannels;
        } finally {
            window.__suppressDecodeFallback = false;
            try { ctx.close(); } catch(_){}
        }
    } catch(_){ /* partial data or unsupported codec — leave fields empty */ }
    return info;
}

async function probeFFmpeg(file, info){
    // Only fire if the processor is already loaded. Don't pay the 30 MB WASM
    // init tax just to fill in a codec-name chip — the tool will load it when
    // processing starts and subsequent file-info probes reuse the loaded core.
    if (!window.FFmpegProcessor || !window.FFmpegProcessor.isReady || typeof window.FFmpegProcessor.probeStreaming !== 'function') return info;
    if (!window.FFmpegProcessor.isReady()) return info;
    try {
        var log = await window.FFmpegProcessor.probeStreaming(file);
        parseFFmpegLog(log, info);
    } catch(_){}
    return info;
}

function parseFFmpegLog(log, info){
    if (!log) return;
    var br = log.match(/bitrate:\s*(\d+)\s*kb\/s/i);
    if (br) info.bitrate = parseInt(br[1], 10);
    var fps = log.match(/([\d.]+)\s*fps/i);
    if (fps) info.fps = parseFloat(fps[1]);
    var vStream = log.match(/Stream\s+#\d+:\d+.*?Video:\s*(\w+)/i);
    if (vStream) info.vcodec = vStream[1].toUpperCase();
    var aStream = log.match(/Stream\s+#\d+:\d+.*?Audio:\s*(\w+)/i);
    if (aStream) info.acodec = aStream[1].toUpperCase();
    var chLog = log.match(/Audio:.*?(mono|stereo|5\.1|7\.1|2\.1|quad|\d+\s*channels)/i);
    if (chLog && !info.channels) info.channels = chLog[1];
    var srLog = log.match(/Audio:.*?(\d{4,6})\s*Hz/i);
    if (srLog && !info.sampleRate) info.sampleRate = parseInt(srLog[1], 10);
    // Video stream sometimes exposes explicit WxH even when native element missed
    if (info.type === 'video' && (!info.width || !info.height)) {
        var res = log.match(/Video:.*?(\d{3,5})x(\d{3,5})/i);
        if (res) { info.width = parseInt(res[1], 10); info.height = parseInt(res[2], 10); }
    }
}

async function probe(file){
    var info = probeInstant(file);
    // Stage 2 and 3 in parallel — independent browser APIs
    await Promise.all([
        probeNativeMedia(file, info),
        probeWebAudio(file, info),
    ]);
    await probeFFmpeg(file, info);
    return info;
}

// ───── Rendering ─────

function setField(card, field, value, showIfEmpty){
    var el = card.querySelector('[data-field="' + field + '"]');
    if (!el) return;
    if (value == null || value === '' || value === false) {
        if (!showIfEmpty) el.hidden = true;
        el.textContent = '';
        return;
    }
    el.hidden = false;
    el.textContent = value;
}

function applyInfo(card, info){
    // Icons
    var iconsEl = card.querySelector('[data-field="icons"]');
    if (iconsEl) renderIcons(iconsEl, info);

    // Name — always visible, truncated via CSS; tooltip shows full name
    var nameEl = card.querySelector('[data-field="name"]');
    if (nameEl) {
        nameEl.textContent = info.name;
        nameEl.setAttribute('title', info.name);
    }

    setField(card, 'size',       fmtSize(info.size), true);
    setField(card, 'duration',   info.duration ? fmtDuration(info.duration) : '');
    setField(card, 'resolution', (info.width && info.height) ? (info.width + '×' + info.height) : '');
    setField(card, 'fps',        fmtFps(info.fps));
    setField(card, 'bitrate',    fmtBitrate(info.bitrate));
    setField(card, 'vcodec',     info.vcodec ? info.vcodec : '');
    setField(card, 'acodec',     info.acodec ? info.acodec : '');
    setField(card, 'sampleRate', fmtSampleRate(info.sampleRate));
    setField(card, 'channels',   fmtChannels(info.channels));
}

function showWarning(card, msg){
    var warn = card.querySelector('[data-field="warn"]');
    if (!warn) return;
    if (!msg) { warn.hidden = true; return; }
    warn.hidden = false;
    var span = warn.querySelector('span');
    if (span) span.textContent = msg;
}

// ───── Replace-by-drag ─────

function wireReplaceDrop(card, onReplace){
    var dragCounter = 0;
    card.addEventListener('dragenter', function(e){
        if (!e.dataTransfer || !e.dataTransfer.types || e.dataTransfer.types.indexOf('Files') === -1) return;
        e.preventDefault();
        dragCounter++;
        card.classList.add('tfi-drag-over');
    });
    card.addEventListener('dragover', function(e){
        if (e.dataTransfer && e.dataTransfer.types && e.dataTransfer.types.indexOf('Files') !== -1) e.preventDefault();
    });
    card.addEventListener('dragleave', function(e){
        dragCounter--;
        if (dragCounter <= 0) { dragCounter = 0; card.classList.remove('tfi-drag-over'); }
    });
    card.addEventListener('drop', function(e){
        if (!e.dataTransfer || !e.dataTransfer.files || !e.dataTransfer.files[0]) return;
        e.preventDefault();
        dragCounter = 0;
        card.classList.remove('tfi-drag-over');
        if (onReplace) onReplace(e.dataTransfer.files[0]);
    });
}

// ───── Public API ─────

var ToolFileInfo = {
    show: function(idOrEl, file, opts){
        var card = $(idOrEl);
        if (!card) return null;
        opts = opts || {};

        card.hidden = false;
        var info = probeInstant(file);
        applyInfo(card, info);          // render the instant fields immediately

        // Kick off the rest of the probe in the background
        (async function(){
            try {
                await probeNativeMedia(file, info);
                applyInfo(card, info);
                await probeWebAudio(file, info);
                applyInfo(card, info);
                await probeFFmpeg(file, info);
                applyInfo(card, info);
            } catch(_){}
        })();

        // Wire × button
        var removeBtn = card.querySelector('.tfi-remove');
        if (removeBtn && !removeBtn.__tfiWired) {
            removeBtn.__tfiWired = true;
            removeBtn.addEventListener('click', function(){
                card.hidden = true;
                if (opts.onRemove) opts.onRemove();
            });
        }

        // Replace-by-drag
        if (!card.__tfiReplaceWired) {
            card.__tfiReplaceWired = true;
            wireReplaceDrop(card, function(newFile){
                applyInfo(card, probeInstant(newFile));
                if (opts.onReplace) opts.onReplace(newFile);
                else if (opts.onRemove) { opts.onRemove(); /* let tool reset, then user picks from zone */ }
            });
        }

        return info;
    },

    hide: function(idOrEl){
        var card = $(idOrEl);
        if (card) card.hidden = true;
    },

    warn: function(idOrEl, msg){
        var card = $(idOrEl);
        if (card) showWarning(card, msg);
    },

    probe: probe,

    version: '1.0.0',
};

window.ToolFileInfo = ToolFileInfo;

})();
